import { Button, Dialog, DialogBody, DialogContent, DialogFooter, Text } from '@civilgrid/ui/components';
import type React from 'react';
import { useState } from 'react';
import { type AgencySubGeometry, archiveAgencySubGeometry as apiArchiveAgencySubGeometry } from '~/api/civilsearch';
import { capturePosthogEvent, POSTHOG_EVENTS } from '~/common/posthogEvents';
import { Notify } from '~/components2/Notify';
import { subGeometryDisplayName } from './helpers';

// CG-480: confirm-before-archive. Archiving soft-deletes the sub region and reverts its
// scoped contacts/links/notes to the agency level (their scope FK is cleared). The dialog
// surfaces that blast radius so the operator sees what loses its scope before committing.
export default function ArchiveSubGeometryDialog({
  isOpen,
  onOpenChange,
  onCloseAutoFocus,
  agencyId,
  subGeometry,
  onArchived,
}: {
  isOpen: boolean;
  onOpenChange: (open: boolean) => void;
  /** Picks where focus lands on close; the popover or popup that opened the dialog is gone by then. */
  onCloseAutoFocus?: (event: Event) => void;
  agencyId: string;
  subGeometry: AgencySubGeometry;
  onArchived: () => void;
}) {
  const [isArchiving, setIsArchiving] = useState(false);
  const { contacts, links, notes } = subGeometry;
  const affectedCount = contacts.length + links.length + notes.length;
  const name = subGeometryDisplayName(subGeometry.name);

  const onConfirm = async () => {
    setIsArchiving(true);
    try {
      await apiArchiveAgencySubGeometry(agencyId, subGeometry.id);
      capturePosthogEvent(POSTHOG_EVENTS.civilsearchAgencySubGeometryArchived, {
        agency_id: agencyId,
        sub_geometry_id: subGeometry.id,
        contacts_cleared: contacts.length,
        links_cleared: links.length,
        notes_cleared: notes.length,
      });
      Notify.info({ message: 'Sub region archived' });
      onArchived();
    } catch (e) {
      console.error('Error archiving sub region', e);
      Notify.error({ message: 'Error archiving sub region' });
      setIsArchiving(false);
    }
  };

  return (
    <Dialog open={isOpen} onOpenChange={onOpenChange}>
      <DialogContent
        title={`Archive ${name}`}
        onCloseAutoFocus={onCloseAutoFocus}
        className="z-[110] w-auto min-w-min max-w-[75vw] w-[28rem] max-w-[90vw]"
        overlayClassName="z-[100]"
        data-testid={`agency-sub-geometries-archive-dialog-${subGeometry.id}`}
      >
        <DialogBody className="text-sm">
          <div className="flex flex-col gap-3">
            <Text className="text-sm">
              Archiving removes this sub region from the agency. Data requests that already referenced it are kept for
              audit.
            </Text>
            <Text className="text-sm">
              {affectedCount === 0
                ? 'No contacts, links, or notes are scoped to it.'
                : 'These will be reverted to the agency level (their sub region scope is cleared):'}
            </Text>

            {contacts.length > 0 && (
              <ArchiveSection label={`Contacts (${contacts.length})`} testId="agency-sub-geometries-archive-contacts">
                {contacts.map((c) => (
                  <div
                    key={c.id}
                    className="flex flex-col"
                    data-testid={`agency-sub-geometries-archive-contact-${c.id}`}
                  >
                    <Text className="text-xs">
                      {c.firstName} {c.lastName}
                      {c.title ? ` — ${c.title}` : ''}
                    </Text>
                    <Text className="text-xs text-grey-60">{c.email}</Text>
                  </div>
                ))}
              </ArchiveSection>
            )}

            {links.length > 0 && (
              <ArchiveSection label={`Links (${links.length})`} testId="agency-sub-geometries-archive-links">
                {links.map((l) => (
                  <div key={l.id} className="flex flex-col" data-testid={`agency-sub-geometries-archive-link-${l.id}`}>
                    <Text className="text-xs">
                      {l.title} <span className="text-grey-60">({l.type})</span>
                    </Text>
                    <Text className="truncate text-xs text-grey-60">{l.url}</Text>
                  </div>
                ))}
              </ArchiveSection>
            )}

            {notes.length > 0 && (
              <ArchiveSection label={`Notes (${notes.length})`} testId="agency-sub-geometries-archive-notes">
                {notes.map((n) => (
                  <Text
                    key={n.id}
                    className="whitespace-pre-wrap text-xs"
                    data-testid={`agency-sub-geometries-archive-note-${n.id}`}
                  >
                    {n.notes}
                  </Text>
                ))}
              </ArchiveSection>
            )}
          </div>
        </DialogBody>
        <DialogFooter>
          <Button
            variant="secondary"
            onClick={() => onOpenChange(false)}
            disabled={isArchiving}
            data-testid="agency-sub-geometries-archive-cancel"
          >
            Cancel
          </Button>
          <Button
            variant="danger"
            onClick={onConfirm}
            disabled={isArchiving}
            data-testid="agency-sub-geometries-archive-confirm"
          >
            {isArchiving ? 'Archiving…' : 'Archive sub region'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

function ArchiveSection({ label, testId, children }: { label: string; testId: string; children: React.ReactNode }) {
  return (
    <div className="flex flex-col gap-1">
      <Text className="text-xs font-medium text-grey-70">{label}</Text>
      <div className="flex flex-col gap-1.5" data-testid={testId}>
        {children}
      </div>
    </div>
  );
}
